import { RouletteOptionsClasses } from 'betgames-contract-js';

export interface IBaseTokenData {
    operatorId: string;
    playerId: string;
    gameId: string;
    mode: string;
    currency: string;
    currentToken?: string;
    gameRound?: string;
    walletMode?: string;
    language: string;
    currencyMultiplier: number;
}

export interface IBalanceDetail {
    amount: number;
    currency: string;
    type: string;
}

export interface IBalance {
    totalBalance: number;
    mode: string;
    currency: string;
    balances: IBalanceDetail[];
}

export enum IForcedOrientation {
    none = 0,
    landscape = 1,
    portrait = 2,
}

export interface IRGSGameSettings {
    autoplayEnabled: boolean;
    autoplayLossLimitRequired: boolean;
    customStakes?: number[];
    displayCoins: boolean;
    displayJackpotOdds: boolean;
    displayRTP: boolean;
    displayXRTP: boolean;
    forceOrientation: IForcedOrientation;
    gambleEnabled: boolean;
    historyURL: string;
    homeURL: string;
    loadMsg: string;
    maxExposure?: number;
    maxStake?: number;
    maxPackageStake?: number;
    minStake?: number;
    minLoadTime?: number;
    minSpinTime?: number;
    multipleInstancesAllowed: boolean;
    rcDisplayWinLoss: boolean;
    rcEnabled: boolean;
    rcInterval?: number;
    stopEnabled: boolean;
    topupURL: string;
    turboEnabled: boolean;
    homeEnabled: boolean;
    dynamicMinSpinTime: boolean;
    redirectTarget: 'self' | 'parent' | 'top';
    packageBuyEnabled: boolean;
    defaultStakeIndex?: number;
}

export interface IGameInitializationResponse {
    tokenData: IBaseTokenData;
    balance: IBalance;
    backendUrl: string;
    refreshUrl: string;
    logoutUrl: string;
    webSocketUrl: string;
    token: string;
    gameSettings: IRGSGameSettings;
    operatorProtocol: number;
}

export interface IExtendedTokenData extends IBaseTokenData {
    ts: string;
    environment: string;
    /**
     * Current player balance in minor currency units. Optional because older BE versions might not send it.
     */
    balance?: number;
}

export interface IGameConfig {
    config: {
        stakes: number[];
        currency: string;
        currencyMultiplier: number;
        expectedRtp: number;
        oneHundredXOdds?: number;
        maxWinOdds?: number;
        maxPayout?: number;
        maxPayoutOdds: number;
        maxExposure?: number;
        odds: {
            oddValues: Partial<Record<RouletteOptionsClasses, number>>;
            oddItems: Partial<Record<RouletteOptionsClasses, number[] | number[][]>>;
        };
        /**
         * Optional array returned by Lucky-Lenny carrying the currently open
         * bonus-game symbols.  Present only when the backend instructs the FE to
         * resume an unfinished GAMEBOARD round.
         */
        symbols?: number[];
    };
    tokenData: IExtendedTokenData;
    /**
     * Optional field returned by some RGS games (e.g. Lucky-Lenny) that tells
     * the FE which bonus feature should start immediately after loading.
     * Possible values: "standard" | "wheel" | "gameboard" | "respin".
     */
    nextAction?: string;
    lastRoundResult?: {
        symbols?: number[];
        type?: string;
    };
}

export interface IGameRoundInfo {
    hash: string;
    status: string;
    stake: number;
    mode: string;
    currency: string;
    game: string;
    baseStake: number;
    currencyMultiplier: number;
}

export interface IGameStateDebit {
    hash: string;
    type: 'debit';
    result: {
        winAmount: number;
        info: {
            result: number;
            betData: {
                bets: {
                    //  TODO odd
                    odd: RouletteOptionsClasses;
                    win: number;
                    item: number[];
                    stake: number;
                    result: 'won' | 'lost';
                    oddValue: number;
                }[];
            };
            rngCallId: string;
        };
    };
    processedOn: string;
}

export interface IGameStateCredit {
    hash: string;
    type: 'credit';
    result: {
        info: unknown;
    };
    collected: number;
    reason: string;
    processedOn: string;
}

export interface IPlayResponse {
    tokenData: IExtendedTokenData;
    balance: IBalance;
    amountToCollect: number;
    amountCredited: number;
    gameRoundInfo: IGameRoundInfo;
    gameState: [IGameStateDebit, IGameStateCredit?];
}
